import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Clock, ChevronRight, LogOut } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [activeTab, setActiveTab] = useState('history');

  // Mock data
  const verificationHistory = [
    { id: 1, firm: 'Acme Legal Ltd', date: '2024-01-15', status: 'APPROVED', type: 'KYC' },
    { id: 2, firm: 'Smith & Co', date: '2023-12-10', status: 'APPROVED', type: 'KYC' },
  ];

  const activeShares = [
    { id: 1, firm: 'Acme Legal Ltd', sharedOn: '2024-01-15', expiresOn: '2025-01-15' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 safe-area-top">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold ml-2">My Profile</h1>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-navy-900 text-white p-6">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-semibold">{user?.firstName} {user?.lastName}</h2>
            <p className="text-gray-400">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            History
          </button>
          <button
            onClick={() => setActiveTab('shares')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'shares'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            Active Shares
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4">
        {activeTab === 'history' ? (
          <div className="space-y-4">
            {verificationHistory.map((item) => (
              <div key={item.id} className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{item.firm}</p>
                      <p className="text-sm text-gray-500">{item.type} Verification</p>
                      <p className="text-sm text-gray-400 mt-1">{item.date}</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    {item.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {activeShares.length > 0 ? (
              activeShares.map((share) => (
                <div key={share.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{share.firm}</p>
                        <p className="text-sm text-gray-500">Shared on {share.sharedOn}</p>
                        <p className="text-sm text-gray-400">Expires {share.expiresOn}</p>
                      </div>
                    </div>
                    <button className="text-red-600 text-sm font-medium">
                      Revoke
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No active shares</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 bg-white border-t border-gray-200 safe-area-bottom">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center text-red-600 font-medium py-3"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default Profile;
